import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import useCatalogs from 'hooks/useCatalogs';

import {
  setValueSearch,
  setOptionFilter,
  setChipData,
  getValueSearch,
  getOptionFilter,
  getChipData,
} from 'store/clients';

import {
  getCatalogsByData,
  getDataByFilters,
} from 'components/Clients/Filters/helpers';
import {
  KEY_FILTER_OPTIONS,
  filterOptions,
} from 'components/Clients/Filters/constants';

import { ACTIVE_STATUS } from 'constants/globals';

import { ascSort, arrayValidation } from 'utils/array';
import type { Client } from 'api/client/models';
import type LabelValue from 'commons/modules/label-value';
import type { ChipDataType } from 'store/types/clients-store';

const useFilterClients = (
  dataOriginal: Client[],
  setData: (value: Client[]) => void,
) => {
  const dispatch = useDispatch();

  const valueSearch = useSelector(getValueSearch);
  const optionFilter = useSelector(getOptionFilter);
  const chipData = useSelector(getChipData);

  const [searchOptions, setSearchOptions] = useState<LabelValue[]>([]);
  const [inputValue, setInputValue] = useState<string>('');

  const [clientOptions, setClientOptions] = useState<LabelValue[]>([]);
  const [cveInternalOptions, setCveInternalOptions] = useState<LabelValue[]>(
    [],
  );
  const [accountOptions, setAccountOptions] = useState<LabelValue[]>([]);
  const [legalRepresentativeOptions, setLegalRepresentativeOptions] = useState<
    LabelValue[]
  >([]);

  const { accountStatus, profiles, brokerageHouses } = useCatalogs();

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDeleteAll = () => {
    dispatch(setChipData([]));
  };

  const handleDelete = (chipToDelete: ChipDataType) => () => {
    const newChipData = chipData.filter(
      (chip) => chip.value !== chipToDelete.value,
    );

    dispatch(setChipData(newChipData));
  };

  const handleFilterOption = (
    e: React.ChangeEvent<any>,
    selectedFilter: any,
  ) => {
    if (!selectedFilter) return;
    inputRef.current?.focus();
    dispatch(setOptionFilter(selectedFilter));
  };

  const processSearch = () => {
    if (arrayValidation(dataOriginal) && arrayValidation(chipData)) {
      const newData = getDataByFilters(chipData, dataOriginal);
      setData(newData);
    } else {
      const newData =
        dataOriginal.filter((item) => item.cveStatus === ACTIVE_STATUS) || [];
      setData(newData);
    }
  };

  const handleChangeSearch = (
    e: React.SyntheticEvent,
    selected: LabelValue,
  ) => {
    dispatch(setValueSearch(null));
    setInputValue('');
    dispatch(
      setChipData([
        ...chipData,
        { ...selected, filterBy: optionFilter?.value },
      ]),
    );
  };

  const setCatalogOptions = () => {
    const { clients, legalRepresentative, cveInternal, accounts } =
      getCatalogsByData(dataOriginal);
    setClientOptions(clients);
    setLegalRepresentativeOptions(legalRepresentative);
    setCveInternalOptions(cveInternal);
    setAccountOptions(accounts);
  };

  useEffect(() => {
    processSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipData]);

  React.useEffect(() => {
    if (arrayValidation(dataOriginal)) {
      processSearch();
      setCatalogOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataOriginal]);

  useEffect(() => {
    if (!optionFilter) {
      dispatch(setOptionFilter(filterOptions[0]));
      return;
    }
    dispatch(setValueSearch(null));
    setInputValue('');
    switch (optionFilter.value) {
      case KEY_FILTER_OPTIONS.client:
        setSearchOptions(clientOptions);
        break;
      case KEY_FILTER_OPTIONS.bank:
        setSearchOptions(brokerageHouses.values);
        break;
      case KEY_FILTER_OPTIONS.status:
        setSearchOptions(accountStatus.values);
        break;
      case KEY_FILTER_OPTIONS.profile:
        setSearchOptions(ascSort(profiles.values, 'label'));
        break;
      case KEY_FILTER_OPTIONS.legalRepresentative:
        setSearchOptions(legalRepresentativeOptions);
        break;
      case KEY_FILTER_OPTIONS.cveInternal:
        setSearchOptions(cveInternalOptions);
        break;
      case KEY_FILTER_OPTIONS.account:
        setSearchOptions(accountOptions);
        break;
      default:
        setSearchOptions([]);
        break;
    }
  }, [
    dispatch,
    optionFilter,
    clientOptions,
    accountStatus,
    profiles,
    legalRepresentativeOptions,
    cveInternalOptions,
    accountOptions,
    setSearchOptions,
    brokerageHouses,
  ]);

  useEffect(() => {
    if (!optionFilter) {
      dispatch(setOptionFilter(filterOptions[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    inputRef,
    searchOptions,
    inputValue,
    valueSearch,
    optionFilter,
    chipData,
    setSearchOptions,
    setInputValue,
    setValueSearch,
    handleFilterOption,
    handleDelete,
    handleDeleteAll,
    handleChangeSearch,
  };
};

export default useFilterClients;
